// --- React & Redux
import React from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
// --- Actions & Hooks
import { useLicensingJobsData } from "./hooks/useLicensingJobsData";
import { useRecursiveTimeout } from "./hooks/useRecursiveTimeout";
import { useUploadDialog } from "../../components/dialogs/dialogs/upload";
import { useLoading } from "../../utils/hooks/useLoading";
import { useToasts } from "../../utils/hooks/useToasts";
import { usePermissions } from "../../utils/permissions";
import { useStoredItemsPerPageSync } from "tap/utils/hooks/useStoredItemsPerPageSync.js";
import { PAGINATION_STORAGE_KEYS } from "tap/utils/paginationPreferences.js";
// --- External UI Libraries
import Button from "@dtci/spellbook/Button";
import Typography from "@dtci/spellbook/Typography";
import { Col, Row } from "antd";
// --- Components
import { PaginationBar } from "../../components/PaginationBar";
import ToastNotificationProvider, {
  useToastNotification,
} from "../../components/ToastNotifications";
import { JobsGrid } from "./components/grid";
import { JobsSearchForm } from "./components/search-form";
import TwoRowsLayout from "./two-row";
import { ViewLicensingJobDrawer } from "./components/drawers/view";
import { EditLicensingJobDrawer } from "./components/drawers/create-edit";
import { UploadTasksDrawer } from "./components/drawers/upload-tasks";
import { JobStatusModal } from "./components/modals/status/index.jsx";
// --- Utilities
import { tapApi } from "../../api";
import { getItemRange } from "../../utils/pagination";
import { parseSearchParams, updateSearchParams } from "../../utils/urlParams";
import { exportToCsvWithHeaders } from "../../utils/exportToCsv";
import {
  LICENSING_JOBS_EXPORT_RANGE,
  EXPORT_HEADERS,
  EXPORT_RENAMED_HEADERS,
  transformJobForExport,
} from "./config/exportConfig";
import { validateFiles } from "../../utils/fileValidation";
import {
  getAsyncUploadJobTrackerId,
  getUploadErrorMessage,
  isApiErrorResponse,
  isBulkIngestValidationFailure,
  mapBulkIngestErrorsToUploadTasks,
} from "../../utils/bulkIngestResult";
import { startJobStatusTracker } from "../../utils/jobStatusTracker";
// --- Styles
import "./styles.scss";

// Auto-refresh interval in milliseconds (15 seconds)
const AUTO_REFRESH_INTERVAL = 15000;

/**
 * Licensing Jobs Page
 *
 * Features:
 * - Displays a paginated list of jobs
 * - Provides search and export functionality
 * - Handles loading and toast notifications
 * - Uses a two-row layout for search and grid
 *
 * @returns {Element}
 * @constructor
 */
export const LicensingJobsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setLoadingTrue, setLoadingFalse } = useLoading();
  const { showErrorToast, showSuccessToast } = useToasts();
  const { createJobProgressToastHandler } = useToastNotification();
  const { fetchLicensingJobs, updateJobs, itemsPerPage, licJobRange } =
    useLicensingJobsData();
  const { openUploadDialog } = useUploadDialog();
  const { isTapUser } = usePermissions();

  const {
    totalPages,
    currentPage,
    totalItems,
    pageSizeOptions,
    isJobViewModalOpen,
  } = useSelector((state) => state.licensingJobs);

  useStoredItemsPerPageSync({
    storageKey: PAGINATION_STORAGE_KEYS.licensingJobs,
    itemsPerPage,
    rangeKey: "licJobRange",
    onSync: updateJobs,
  });

  const handleChange = async (pageNumber) => {
    if (pageNumber === currentPage) return;

    const nextPageRange = getItemRange(pageNumber, itemsPerPage);
    const data = await fetchLicensingJobs(nextPageRange);

    if (data) {
      updateJobs({
        jobs: data?.content,
        totalItems: data?.total,
        totalPages: Math.ceil(data?.total / data?.size) || 0,
        currentPage: data?.pageNumber,
        licJobRange: nextPageRange,
      });
    }
  };

  const handlePageSizeChange = (newSize) => {
    const currentParams = parseSearchParams(location.search);
    updateSearchParams(location, navigate, {
      ...currentParams,
      pageSize: newSize,
    });
  };

  const handleRefresh = () => {
    updateJobs({ refreshJobs: true });
  };

  // Auto-refresh is enabled when the job view modal is not open
  useRecursiveTimeout(
    handleRefresh,
    AUTO_REFRESH_INTERVAL,
    !isJobViewModalOpen,
  );

  const handleExport = async () => {
    const data = await fetchLicensingJobs(LICENSING_JOBS_EXPORT_RANGE);

    if (!data) return;

    const forExport = data?.content?.map(transformJobForExport);
    exportToCsvWithHeaders(
      forExport,
      EXPORT_HEADERS,
      EXPORT_RENAMED_HEADERS,
      "licensing",
    );
  };

  const onOpenUploadDialog = (data) => {
    openUploadDialog(
      data,
      async ({ file }) => {
        if (file) {
          const files = Array.isArray(file) ? file : [file];
          const validation = validateFiles(files);
          if (!validation.valid) {
            console.error("File validation failed:", validation.error);
            alert(validation.error);
            return;
          }

          const formData = new FormData();
          files.forEach((f) => formData.append("file", f));

          try {
            setLoadingTrue();
            const result = await tapApi.uploadLicensingJob(formData);
            if (isApiErrorResponse(result)) {
              showErrorToast(
                result?.message ||
                  result?.error?.message ||
                  getUploadErrorMessage(result),
              );
              setLoadingFalse();
              return;
            }

            const trackerJobId = getAsyncUploadJobTrackerId(result);
            if (trackerJobId != null) {
              const onJobProgress = createJobProgressToastHandler(trackerJobId);

              startJobStatusTracker(
                trackerJobId,
                async (message, status) => {
                  onJobProgress(message, status);

                  if (status === "success") {
                    updateJobs({ refreshJobs: true });
                    showSuccessToast("Licensing job uploaded successfully.");
                  }

                  setLoadingFalse();
                },
                (jobTasks, jobInfo) => {
                  if (
                    jobInfo?.jobStatus === "COMPLETE" &&
                    (jobInfo?.errorCount ?? 0) === 0
                  ) {
                    return;
                  }

                  updateJobs({
                    isUploadTasksDrawerOpen: true,
                    licensingJobUploadTasks: jobTasks || [],
                  });
                },
              );

              setLoadingFalse();
              return;
            }

            if (isBulkIngestValidationFailure(result)) {
              updateJobs({
                isUploadTasksDrawerOpen: true,
                licensingJobUploadTasks: mapBulkIngestErrorsToUploadTasks(result),
              });
            } else {
              updateJobs({ refreshJobs: true });
              showSuccessToast("Licensing job uploaded successfully.");
            }
          } catch (error) {
            showErrorToast("Error uploading licensing job.");
            console.error("Error uploading licensing job:", error);
          } finally {
            setLoadingFalse();
          }
        }
      },
      "licensingJob",
    );
  };

  const handleCreateJob = () => {
    updateJobs({
      selectedJob: null,
      isEditDrawerOpen: true,
    });
  };

  return (
    <>
      <Row className="licensing-jobs-page-header">
        <Col>
          <Typography variant="T50" className="licensing-jobs-page-title">
            Licensing Jobs
          </Typography>
        </Col>
        <Col className="licensing-jobs-page-actions">
          {isTapUser && (
            <Button
              iconType="fa-solid fa-upload"
              labelText="metrics"
              styleType="link"
              onClick={onOpenUploadDialog}
            >
              Upload
            </Button>
          )}
          <Button
            iconType="fa-solid fa-plus"
            labelText="Refresh"
            styleType="link"
            onClick={handleCreateJob}
          >
            Create
          </Button>
          <Button
            iconType="fa-solid fa-arrow-down-to-line"
            labelText="Export"
            styleType="link"
            onClick={handleExport}
            disabled={totalItems === 0}
          >
            Export
          </Button>
          <Button
            iconType="fa-solid fa-rotate"
            labelText="Refresh"
            styleType="link"
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </Col>
      </Row>
      <TwoRowsLayout>
        <main data-position="top">
          <JobsSearchForm />
        </main>
        <main data-position="bottom">
          <JobsGrid />
        </main>
      </TwoRowsLayout>
      <PaginationBar
        totalPages={totalPages}
        currentPage={currentPage}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={handleChange}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={pageSizeOptions}
      />
      <ViewLicensingJobDrawer />
      <EditLicensingJobDrawer />
      <UploadTasksDrawer onOpenUploadDialog={onOpenUploadDialog} />
      <JobStatusModal />
      <ToastNotificationProvider />
    </>
  );
};
